import React from "react";
import type { FieldError, FieldValues, Path, RegisterOptions, UseFormRegister } from "react-hook-form";
import { RequiredIndicator } from "./requiredIndicator";

interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface DropdownInputProps<TFormValues extends FieldValues = FieldValues> {
  id: string;
  name: Path<TFormValues>;
  label: React.ReactNode;
  options: DropdownOption[];
  placeholder?: string;
  required?: boolean;
  className?: string;
  labelClassName?: string;
  selectClassName?: string;
  register?: UseFormRegister<TFormValues>;
  validation?: RegisterOptions<TFormValues, Path<TFormValues>>;
  error?: FieldError;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  disabled?: boolean;
  ariaDescribedBy?: string;
  /**
   * When true, the field still reflects its error state (border + aria-invalid)
   * but does not render its own inline error message. Useful when a single
   * shared message describes a group of related inputs (e.g. Date of Birth).
   */
  hideErrorMessage?: boolean;
}

const DropdownInput = <TFormValues extends FieldValues = FieldValues>({
  id,
  name,
  label,
  options,
  placeholder = "Select...",
  required = false,
  className = "gcw:w-full",
  labelClassName = "",
  selectClassName = "container-inputs",
  register,
  validation,
  error,
  value,
  onChange,
  disabled = false,
  ariaDescribedBy,
  hideErrorMessage = false,
}: DropdownInputProps<TFormValues>) => {
  const registration = register ? register(name, validation) : undefined;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    registration?.onChange(e);
    onChange?.(e);
  };

  return (
    <div className={className}>
      <label className={`gcw:block gcw:mb-2 ${labelClassName}`} htmlFor={id}>
        {label}
        {required && <RequiredIndicator />}
      </label>
      <select
        id={id}
        className={`${selectClassName} ${error ? "gcw:border-error" : ""}`}
        name={registration?.name ?? name}
        value={value}
        onChange={handleChange}
        onBlur={registration?.onBlur}
        ref={registration?.ref}
        disabled={disabled}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={
          [error && !hideErrorMessage ? `${id}-error` : undefined, ariaDescribedBy].filter(Boolean).join(" ") ||
          undefined
        }
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            aria-disabled={option.disabled ? "true" : undefined}
          >
            {option.label}
          </option>
        ))}
      </select>
      {error && !hideErrorMessage && (
        <div id={`${id}-error`} className="gcw:text-error gcw:text-md gcw:mt-1" role="alert">
          {error.message}
        </div>
      )}
    </div>
  );
};

export type { DropdownOption, DropdownInputProps };
export default DropdownInput;
